import React, { useState, useEffect } from 'react';
import { Badge, BadgeFormData, Group } from '../types';
import CustomDropdown from '../../../UI/CustomDropdown';
import sidePanelStyles from '../../../../styles/SidePanel.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import customDropdownStyles from '../../../../styles/CustomDropdown.module.css';
import styles from '../../../../styles/BadgesAndAchievements.module.css';
import settingsStyles from '../../../../styles/SettingsTab.module.css';

interface AddEditBadgePanelProps {
  mode: 'create' | 'edit';
  badge?: Badge | null;
  formData: BadgeFormData;
  groups: Group[];
  isDragOver: boolean;
  onFormDataChange: (data: BadgeFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  onImageUpload: (file: File) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
}

const BADGE_TYPES = [
  { value: 'points', label: 'Points Achievement', icon: '💰', description: 'Awarded for reaching point milestones' },
  { value: 'tasks', label: 'Task Completion', icon: '✅', description: 'Awarded for completing tasks' },
  { value: 'quality', label: 'Quality Performance', icon: '⭐', description: 'Awarded for high quality work' },
  { value: 'streak', label: 'Activity Streak', icon: '🔥', description: 'Awarded for consistent daily activity' },
];

const SCOPE_OPTIONS = [
  { value: 'global', label: 'Global' },
  { value: 'group', label: 'Group' },
];

const AddEditBadgePanel: React.FC<AddEditBadgePanelProps> = ({
  mode,
  formData,
  groups,
  isDragOver,
  onFormDataChange,
  onSave,
  onCancel,
  onImageUpload,
  onDrop,
  onDragOver,
  onDragLeave,
}) => {
  const [imageFile, setImageFile] = useState<File | null>(null);

  // Reset imageFile when formData.image is cleared (e.g., when removing image)
  useEffect(() => {
    if (!formData.image) {
      setImageFile(null);
    }
  }, [formData.image]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      onImageUpload(file);
    }
  };

  const handleDropWithFile = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      onImageUpload(file);
    }
    onDrop(event);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    onFormDataChange({ ...formData, image: '' });
  };

  const getBadgeTypeOptions = () => {
    return BADGE_TYPES.map(type => ({
      value: type.value,
      label: type.label,
    }));
  };

  const getFilteredGroups = () => {
    return groups.map(group => ({
      value: group.id,
      label: group.name,
    }));
  };

  // Get relevant criteria fields based on badge type
  const getRelevantCriteriaFields = () => {
    const badgeType = formData.badge_type;
    const fields: string[] = [];
    
    switch (badgeType) {
      case 'points':
        fields.push('points_threshold');
        break;
      case 'tasks':
        fields.push('tasks_completed');
        break;
      case 'quality':
        fields.push('quality_threshold');
        break;
      case 'streak':
        fields.push('streak_days');
        break;
      default:
        // For unknown types, show no fields (user must select a valid type)
        break;
    }
    
    return fields;
  };

  const relevantFields = getRelevantCriteriaFields();

  return (
    <form className={sidePanelStyles.form} onSubmit={(e) => {
      e.preventDefault();
      onSave();
    }}>
      <div className={sidePanelStyles.formGroup}>
        <label className={sidePanelStyles.formLabel}>
          Badge Name <span className={sidePanelStyles.required}>*</span>
        </label>
        <input
          type="text"
          className={sidePanelStyles.formControl}
          value={formData.name}
          onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
          placeholder="Enter badge name"
          required
        />
      </div>

      <div className={sidePanelStyles.formGroup}>
        <label className={sidePanelStyles.formLabel}>
          Description <span className={sidePanelStyles.required}>*</span>
        </label>
        <textarea
          className={sidePanelStyles.formControl}
          value={formData.description}
          onChange={(e) => onFormDataChange({ ...formData, description: e.target.value })}
          placeholder="Enter badge description"
          rows={3}
          required
        />
      </div>

      <div className={sidePanelStyles.formGroup}>
        <label className={sidePanelStyles.formLabel}>
          Badge Type <span className={sidePanelStyles.required}>*</span>
        </label>
        <CustomDropdown
          options={getBadgeTypeOptions()}
          value={formData.badge_type}
          onChange={(value) => onFormDataChange({ 
            ...formData, 
            badge_type: Array.isArray(value) ? value[0] : value 
          })}
          placeholder="Select badge type"
          className={customDropdownStyles.sizeFull}
        />
        {formData.badge_type && (
          <div className={sidePanelStyles.formHelp} style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--gray)' }}>
            {BADGE_TYPES.find(t => t.value === formData.badge_type)?.description}
          </div>
        )}
      </div>

      <div className={sidePanelStyles.formGroup}>
        <label className={sidePanelStyles.formLabel}>
          Scope <span className={sidePanelStyles.required}>*</span>
        </label>
        <CustomDropdown
          options={SCOPE_OPTIONS}
          value={formData.scope}
          onChange={(value) => onFormDataChange({ 
            ...formData, 
            scope: (Array.isArray(value) ? value[0] : value) as 'global' | 'group' 
          })}
          placeholder="Select scope"
          className={customDropdownStyles.sizeFull}
        />
        <div className={sidePanelStyles.formHelp}>
          {formData.scope === 'global' 
            ? 'This badge will be available across all groups' 
            : 'This badge will be specific to the selected group'}
        </div>
      </div>

      {formData.scope === 'group' && (
        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>
            Group <span className={sidePanelStyles.required}>*</span>
          </label>
          <CustomDropdown
            options={getFilteredGroups()}
            value={formData.group_id}
            onChange={(value) => onFormDataChange({ 
              ...formData, 
              group_id: Array.isArray(value) ? value[0] : value 
            })}
            placeholder="Select group"
            className={customDropdownStyles.sizeFull}
          />
        </div>
      )}

      <div className={sidePanelStyles.formGroup}>
        <label className={sidePanelStyles.formLabel}>Badge Image</label>
        <div className={settingsStyles.fileUploader}>
          {!formData.image ? (
            <div
              className={`${settingsStyles.uploadArea} ${isDragOver ? settingsStyles.dragover : ''}`}
              onDrop={handleDropWithFile}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => document.getElementById('image-input')?.click()}
            >
              <div className={settingsStyles.uploadIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21,15 16,10 5,21"></polyline>
                </svg>
              </div>
              <div className={settingsStyles.uploadText}>
                <div className={settingsStyles.uploadTitle}>Click to upload badge image</div>
                <div className={settingsStyles.uploadSubtitle}>PNG, JPG, GIF up to 10MB</div>
              </div>
            </div>
          ) : (
            <div className={settingsStyles.filePreview}>
              <img 
                src={formData.image} 
                alt="Badge preview"
                style={{
                  objectFit: formData.image.includes('data:image/svg+xml') ? 'contain' : 'contain'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className={settingsStyles.fileInfo}>
                <div className={settingsStyles.fileName}>{imageFile?.name || 'Badge image'}</div>
                <div className={settingsStyles.fileSize}>{imageFile ? formatFileSize(imageFile.size) : ''}</div>
              </div>
              <button
                type="button"
                className={settingsStyles.removeFile}
                onClick={handleRemoveImage}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          )}
          <input
            id="image-input"
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
        </div>
        <div className={settingsStyles.formHelp}>Recommended size: 200x200px or larger</div>
      </div>

      {/* Criteria Configuration - Only show relevant fields based on badge type */}
      {formData.badge_type && (
        <div className={sidePanelStyles.configSection}>
          <h4 className={sidePanelStyles.sectionTitleSmall}>Award Criteria</h4>
          <div className={sidePanelStyles.formHelp} style={{ marginBottom: '1rem' }}>
            Set the threshold that users must reach to earn this badge
          </div>
          
          {relevantFields.includes('points_threshold') && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Points Required <span className={sidePanelStyles.required}>*</span>
              </label>
              <input
                type="number"
                className={sidePanelStyles.formControl}
                min="0"
                value={formData.criteria_config.points_threshold || 0}
                onChange={(e) => onFormDataChange({
                  ...formData,
                  criteria_config: {
                    ...formData.criteria_config,
                    points_threshold: parseInt(e.target.value) || 0
                  }
                })}
                placeholder="e.g., 1000"
                required={formData.badge_type === 'points'}
              />
              <div className={sidePanelStyles.formHelp}>
                Minimum total points required to earn this badge
              </div>
            </div>
          )}

          {relevantFields.includes('tasks_completed') && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Tasks Completed <span className={sidePanelStyles.required}>*</span>
              </label>
              <input
                type="number"
                className={sidePanelStyles.formControl}
                min="0"
                value={formData.criteria_config.tasks_completed || 0}
                onChange={(e) => onFormDataChange({
                  ...formData,
                  criteria_config: {
                    ...formData.criteria_config,
                    tasks_completed: parseInt(e.target.value) || 0
                  }
                })}
                placeholder="e.g., 50"
                required={formData.badge_type === 'tasks'}
              />
              <div className={sidePanelStyles.formHelp}>
                Minimum number of completed tasks required
              </div>
            </div>
          )}

          {relevantFields.includes('quality_threshold') && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Quality Score (%) <span className={sidePanelStyles.required}>*</span>
              </label>
              <input
                type="number"
                className={sidePanelStyles.formControl}
                min="0"
                max="100"
                value={formData.criteria_config.quality_threshold || 0}
                onChange={(e) => onFormDataChange({
                  ...formData,
                  criteria_config: {
                    ...formData.criteria_config,
                    quality_threshold: parseInt(e.target.value) || 0
                  }
                })}
                placeholder="e.g., 85"
                required={formData.badge_type === 'quality'}
              />
              <div className={sidePanelStyles.formHelp}>
                Minimum average quality score percentage (0-100)
              </div>
            </div>
          )}

          {relevantFields.includes('streak_days') && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Consecutive Days <span className={sidePanelStyles.required}>*</span>
              </label>
              <input
                type="number"
                className={sidePanelStyles.formControl}
                min="0"
                value={formData.criteria_config.streak_days || 0}
                onChange={(e) => onFormDataChange({
                  ...formData,
                  criteria_config: {
                    ...formData.criteria_config,
                    streak_days: parseInt(e.target.value) || 0
                  }
                })}
                placeholder="e.g., 30"
                required={formData.badge_type === 'streak'}
              />
              <div className={sidePanelStyles.formHelp}>
                Minimum consecutive days of activity required
              </div>
            </div>
          )}

        </div>
      )}

      <div style={{ 
        
      }}>
        <div className={settingsStyles.toggleContainer}>
          <label className={settingsStyles.toggleLabel} htmlFor="is_active">
            <div className={settingsStyles.toggleTitle}>Active</div>
            <div className={settingsStyles.toggleDescription}>Badge can be awarded to users</div>
          </label>
          <label className={settingsStyles.toggleSwitch}>
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => onFormDataChange({ ...formData, is_active: e.target.checked })}
            />
            <span className={settingsStyles.toggleSlider}></span>
          </label>
        </div>

      </div>

      <div className={sidePanelStyles.formActions}>
        <button
          type="button"
          onClick={onCancel}
          className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
          disabled={
            !formData.name || 
            !formData.description || 
            !formData.badge_type ||
            (formData.scope === 'group' && !formData.group_id) ||
            (formData.badge_type === 'points' && (!formData.criteria_config.points_threshold || formData.criteria_config.points_threshold <= 0)) ||
            (formData.badge_type === 'tasks' && (!formData.criteria_config.tasks_completed || formData.criteria_config.tasks_completed <= 0)) ||
            (formData.badge_type === 'quality' && (!formData.criteria_config.quality_threshold || formData.criteria_config.quality_threshold <= 0)) ||
            (formData.badge_type === 'streak' && (!formData.criteria_config.streak_days || formData.criteria_config.streak_days <= 0))
          }
        >
          {mode === 'create' ? 'Create Badge' : 'Update Badge'}
        </button>
      </div>
    </form>
  );
};

export default AddEditBadgePanel;

